// PROTOTYPE
import * as React from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { mergeRegister } from '@lexical/utils';
import { Button } from 'antd';
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  DRAGSTART_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  type LexicalEditor,
  type NodeKey,
} from 'lexical';
import { useTranslation } from 'react-i18next';
import styles from '../lexical-editor.module.css';
import { $isVideoNode, type VideoSource } from './VideoNode';

interface VideoComponentProps {
  editor: LexicalEditor;
  height: number;
  nodeKey: NodeKey;
  source: VideoSource;
  src: string;
  title: string;
  width: number;
}

export function VideoComponent({
  editor,
  height,
  nodeKey,
  source,
  src,
  title,
  width,
}: VideoComponentProps) {
  const { t } = useTranslation();
  const [isSelected, setSelected, clearSelection] =
    useLexicalNodeSelection(nodeKey);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  const removeVideo = React.useCallback(() => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);

      if ($isVideoNode(node)) {
        node.remove();
      }
    });

    // The object URL outlives the node it fed, so a session of uploading and
    // removing 100 MB clips would hold every one of them until the tab closes.
    if (source === 'upload' && src.startsWith('blob:')) {
      globalThis.URL.revokeObjectURL(src);
    }
  }, [editor, nodeKey, source, src]);

  React.useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        CLICK_COMMAND,
        (event) => {
          const target = event.target;

          if (
            target instanceof HTMLElement &&
            wrapperRef.current?.contains(target)
          ) {
            event.preventDefault();

            if (!event.shiftKey) {
              clearSelection();
            }

            setSelected(!isSelected);
            return true;
          }

          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        (event) => {
          const target = event.target;

          if (
            target instanceof HTMLElement &&
            wrapperRef.current?.contains(target)
          ) {
            event.preventDefault();
            return true;
          }

          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        () => {
          if (!isSelected) {
            return false;
          }

          const selection = $getSelection();

          if ($isNodeSelection(selection)) {
            removeVideo();
            return true;
          }

          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_DELETE_COMMAND,
        () => {
          if (!isSelected) {
            return false;
          }

          const selection = $getSelection();

          if ($isNodeSelection(selection)) {
            removeVideo();
            return true;
          }

          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [clearSelection, editor, isSelected, removeVideo, setSelected, nodeKey]);

  return (
    <div
      className={styles.videoInlineContainer}
      data-selected={isSelected}
      data-testid="videoNode"
      data-video-source={source}
      ref={wrapperRef}
    >
      {source === 'embed' ? (
        <iframe
          allowFullScreen
          className={styles.videoFrame}
          height={height}
          loading="lazy"
          src={src}
          title={title || t('exp_video_player_title')}
          width={width}
        />
      ) : (
        <video
          className={styles.videoPlayer}
          controls
          height={height}
          src={src}
          width={width}
        />
      )}
      {/* A live iframe swallows clicks, so selection needs a shield until the node is selected. */}
      {!isSelected && <div className={styles.videoClickShield} />}
      {editor.isEditable() && (
        <Button
          aria-label={t('exp_video_remove_aria')}
          className={styles.videoRemoveButton}
          data-testid="removeVideoButton"
          icon={<DeleteOutlined />}
          onClick={removeVideo}
          size="small"
          type="text"
        />
      )}
    </div>
  );
}
